(() => {
  const SKIN_SELECTOR = '.ff-kaching-skin';
  const SUBSCRIPTIONS_SELECTOR =
    '.kaching-bundles__subscriptions:not(.kaching-bundles__subscriptions--checkbox):not(.kaching-bundles__subscriptions--link)';

  const DEFAULT_TRANSLATIONS = {
    one_time: 'One time',
    subscribe_and_save: 'Subscribe & save',
    delivery_label: 'Delivery:',
    save_percentage: 'Save {{ percentage }}%',
    best_value: '',
    best_value_default: 'Best value',
    delivery_fallback: 'Every 1 month',
    delivery_frequency_label: 'Delivery frequency',
    delivery_plan_every_month: 'Deliver every month, {{ percentage }}% off',
    delivery_plan_every_months: 'Deliver every {{ count }} months, {{ percentage }}% off',
    delivery_total_savings_every_month:
      'Deliver every month, {{ percentage }}% total savings',
    delivery_total_savings_every_months:
      'Deliver every {{ count }} months, {{ percentage }}% total savings',
  };

  function getTranslations() {
    const script = document.querySelector('[data-ff-kaching-translations]');
    if (!script) return { ...DEFAULT_TRANSLATIONS };

    try {
      return { ...DEFAULT_TRANSLATIONS, ...JSON.parse(script.textContent || '{}') };
    } catch (error) {
      return { ...DEFAULT_TRANSLATIONS };
    }
  }

  function translate(key, replacements = {}, translations = getTranslations()) {
    let value = translations[key] || DEFAULT_TRANSLATIONS[key] || '';
    Object.entries(replacements).forEach(([name, replacement]) => {
      value = value.replace(
        new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'),
        String(replacement)
      );
    });
    return value;
  }

  function getBestValueBadge(skin, translations = getTranslations()) {
    const kachingBadge = getBadgeText(skin);
    if (kachingBadge) return kachingBadge;
    if (translations.best_value) return translations.best_value;
    return translations.best_value_default || DEFAULT_TRANSLATIONS.best_value_default;
  }

  function getSettings(skin) {
    const script =
      skin.querySelector('.kaching-bundles-deal-block-settings') ||
      document.querySelector('.kaching-bundles-deal-block-settings');

    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function getBadgeText(skin) {
    const badge = skin.querySelector('.kaching-bundles__bar-most-popular__content');
    return badge ? badge.textContent.trim() : '';
  }

  function getSelectedBundle(skin) {
    return (
      skin.querySelector(
        '.kaching-bundles__bar--selected:not(.ff-kaching-one-time):not(.ff-kaching-subscription)'
      ) ||
      skin.querySelector(
        '.kaching-bundles__bar[aria-checked="true"]:not(.ff-kaching-one-time):not(.ff-kaching-subscription)'
      )
    );
  }

  function getBundlePrices(skin) {
    const selectedBundle = getSelectedBundle(skin);
    if (!selectedBundle) {
      return { current: '', compare: '' };
    }

    return {
      current: normalizePriceText(
        selectedBundle.querySelector('.kaching-bundles__bar-price')?.textContent || ''
      ),
      compare: normalizePriceText(
        selectedBundle.querySelector('.kaching-bundles__bar-full-price')?.textContent || ''
      ),
    };
  }

  function parsePriceAmount(value) {
    const match = value.match(/[\d,.]+/);
    if (!match) return null;

    const normalized = match[0].includes(',') && !match[0].includes('.')
      ? match[0].replace(',', '.')
      : match[0].replace(/,/g, '');

    const amount = Number.parseFloat(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  function formatPriceLike(referencePrice, amount) {
    if (!referencePrice || amount == null) return '';

    const match = referencePrice.match(/[\d,.]+/);
    if (!match) return referencePrice;

    const prefix = referencePrice.slice(0, match.index);
    const suffix = referencePrice.slice(match.index + match[0].length);
    const usesCommaDecimal =
      match[0].includes(',') && !match[0].includes('.');
    const formatted = usesCommaDecimal
      ? amount.toFixed(2).replace('.', ',')
      : amount.toFixed(2);

    return `${prefix}${formatted}${suffix}`;
  }

  function getDiscountPercent(value) {
    const match = value.match(/(\d+(?:[.,]\d+)?)\s*%/);
    if (!match) return null;

    const normalized = match[1].replace(',', '.');
    const percent = Number.parseFloat(normalized);
    return Number.isFinite(percent) ? percent : null;
  }

  function getPurchaseOptionPrices(skin, nativeBlock, wrapper, nativeTitle) {
    const isSubscription = isNativeSubscriptionSelected(nativeBlock);
    const selectedBundle = getSelectedBundle(skin);
    const bundleKey =
      selectedBundle?.querySelector('.kaching-bundles__bar-title')?.textContent.trim() ||
      '';
    const bundlePrices = getBundlePrices(skin);

    if (wrapper.dataset.bundleKey !== bundleKey) {
      wrapper.dataset.bundleKey = bundleKey;
      delete wrapper.dataset.oneTimePrice;
      delete wrapper.dataset.subscriptionPrice;
      delete wrapper.dataset.ffOneTimeCaptureAttempted;
    }

    // Kaching swaps bar-price when subscription toggles:
    // one-time mode -> bar-price is the bundle one-time price (e.g. €44,60)
    // subscription mode -> bar-price is the subscription price (e.g. €38,00)
    // bar-full-price is the undiscounted compare price (e.g. €45,80), not one-time.
    if (!isSubscription && bundlePrices.current) {
      wrapper.dataset.oneTimePrice = bundlePrices.current;
    }

    if (isSubscription && bundlePrices.current) {
      wrapper.dataset.subscriptionPrice = bundlePrices.current;
    }

    let oneTimePrice = wrapper.dataset.oneTimePrice || '';
    let subscriptionPrice = wrapper.dataset.subscriptionPrice || '';

    if (!subscriptionPrice && oneTimePrice) {
      const discountPercent = getDiscountPercent(nativeTitle);
      const oneTimeAmount = parsePriceAmount(oneTimePrice);
      if (discountPercent != null && oneTimeAmount != null) {
        subscriptionPrice = formatPriceLike(
          oneTimePrice,
          oneTimeAmount * (1 - discountPercent / 100)
        );
      }
    }

    if (!oneTimePrice && !isSubscription && bundlePrices.current) {
      oneTimePrice = bundlePrices.current;
    }

    if (!subscriptionPrice && isSubscription && bundlePrices.current) {
      subscriptionPrice = bundlePrices.current;
    }

    return {
      oneTimePrice,
      subscriptionPrice: subscriptionPrice || oneTimePrice,
    };
  }

  function getCurrentPrice(skin) {
    const selectedBundle = getSelectedBundle(skin);
    const price =
      selectedBundle?.querySelector('.kaching-bundles__bar-price') ||
      skin.querySelector('.kaching-bundles__bar-price');

    return price ? price.textContent.trim() : '';
  }

  function getNativeCheckbox(nativeBlock) {
    return nativeBlock.querySelector('input[type="checkbox"], [role="checkbox"]');
  }

  function isNativeSubscriptionSelected(nativeBlock) {
    const control = getNativeCheckbox(nativeBlock);
    if (control instanceof HTMLInputElement) return control.checked;
    if (control?.getAttribute('aria-checked')) {
      return control.getAttribute('aria-checked') === 'true';
    }

    return (
      nativeBlock.classList.contains('kaching-bundles__subscriptions--selected') ||
      nativeBlock.classList.contains('kaching-bundles__bar--selected') ||
      Boolean(nativeBlock.querySelector('[class*="selected"], [class*="checked"]'))
    );
  }

  function setNativeSubscriptionSelected(nativeBlock, shouldSelect) {
    if (isNativeSubscriptionSelected(nativeBlock) === shouldSelect) return;

    const control = getNativeCheckbox(nativeBlock);
    if (control) {
      control.click();
      return;
    }

    const target =
      nativeBlock.querySelector(
        'label, button, .kaching-bundles__subscriptions__card, .kaching-bundles__subscriptions__checkbox'
      ) || nativeBlock;
    target.click();
  }

  function setText(element, value) {
    if (element && element.textContent !== value) element.textContent = value;
  }

  function normalizePriceText(value) {
    return String(value || '')
      .replace(/\s+/g, '')
      .trim();
  }

  function setPriceText(element, value) {
    if (!element) return;

    const normalized = normalizePriceText(value);
    if (!normalized) {
      element.replaceChildren();
      element.removeAttribute('data-ff-price');
      return;
    }

    if (element.dataset.ffPrice === normalized) return;

    const match = normalized.match(/^([^\d-]*)([\d.,]+)$/);
    if (!match) {
      element.dataset.ffPrice = normalized;
      element.textContent = normalized;
      return;
    }

    const [, currency, amount] = match;
    element.dataset.ffPrice = normalized;
    element.replaceChildren(
      Object.assign(document.createElement('span'), {
        className: 'ff-kaching-purchase-option__currency',
        textContent: currency,
      }),
      Object.assign(document.createElement('span'), {
        className: 'ff-kaching-purchase-option__amount',
        textContent: amount,
      })
    );
  }

  function cleanSubscriptionTitle(value, translations = getTranslations()) {
    return (
      value
        .replace(/\{\{[^}]+\}\}/g, '')
        .replace(/\s+\d+(?:[.,]\d+)?%\s*(?:off)?\s*$/i, '')
        .trim() || translate('subscribe_and_save', {}, translations)
    );
  }

  function getSavingText(value, translations = getTranslations()) {
    const match = value.match(/(\d+(?:[.,]\d+)?)\s*%/);
    return match
      ? translate('save_percentage', { percentage: match[1] }, translations)
      : '';
  }

  function getTotalSavingPercentage(skin, subscriptionPrice) {
    const regularPrice = getBundlePrices(skin).compare;
    const regularAmount = parsePriceAmount(regularPrice);
    const subscriptionAmount = parsePriceAmount(subscriptionPrice);

    if (
      regularAmount == null ||
      subscriptionAmount == null ||
      regularAmount <= 0 ||
      subscriptionAmount >= regularAmount
    ) {
      return null;
    }

    // Compare the selected subscription bundle with buying the same quantity
    // as individual, one-time cans. This matches the saving shown on the
    // bundle card (for example, €97.74 compared with €143.97 = 32%).
    return Math.round(
      ((regularAmount - subscriptionAmount) / regularAmount) * 100
    );
  }

  function getTotalSavingText(
    skin,
    subscriptionPrice,
    nativeTitle,
    translations = getTranslations()
  ) {
    const totalSaving = getTotalSavingPercentage(skin, subscriptionPrice);
    if (totalSaving == null) return getSavingText(nativeTitle, translations);

    return translate('save_percentage', { percentage: totalSaving }, translations);
  }

  function localizeSellingPlanLabel(planId, fallbackText, translations = getTranslations()) {
    const text = String(fallbackText || '').trim();
    const planKey = planId != null ? String(planId) : '';
    const liquidName = planKey ? translations.selling_plans?.[planKey] : '';

    if (liquidName && liquidName !== text) {
      return liquidName;
    }

    const everyMonthMatch = text.match(/^Deliver every month,\s*(\d+(?:[.,]\d+)?)\s*%\s*off$/i);
    if (everyMonthMatch) {
      return (
        translate('delivery_plan_every_month', { percentage: everyMonthMatch[1] }, translations) ||
        text
      );
    }

    const everyMonthsMatch = text.match(
      /^Deliver every (\d+)\s+months?,\s*(\d+(?:[.,]\d+)?)\s*%\s*off$/i
    );
    if (everyMonthsMatch) {
      const count = Number.parseInt(everyMonthsMatch[1], 10);
      if (count === 1) {
        return (
          translate(
            'delivery_plan_every_month',
            { percentage: everyMonthsMatch[2] },
            translations
          ) || text
        );
      }

      return (
        translate(
          'delivery_plan_every_months',
          { count: everyMonthsMatch[1], percentage: everyMonthsMatch[2] },
          translations
        ) || text
      );
    }

    if (liquidName) return liquidName;
    return text;
  }

  function localizeTotalSavingPlanLabel(
    planId,
    fallbackText,
    totalSavingPercentage,
    translations = getTranslations()
  ) {
    const text = String(fallbackText || '').trim();

    if (totalSavingPercentage != null) {
      const everyMonthMatch = text.match(
        /^Deliver every month,\s*\d+(?:[.,]\d+)?\s*%\s*off$/i
      );
      if (everyMonthMatch) {
        return translate(
          'delivery_total_savings_every_month',
          { percentage: totalSavingPercentage },
          translations
        );
      }

      const everyMonthsMatch = text.match(
        /^Deliver every (\d+)\s+months?,\s*\d+(?:[.,]\d+)?\s*%\s*off$/i
      );
      if (everyMonthsMatch) {
        return translate(
          'delivery_total_savings_every_months',
          { count: everyMonthsMatch[1], percentage: totalSavingPercentage },
          translations
        );
      }
    }

    return localizeSellingPlanLabel(planId, text, translations);
  }

  function getDeliveryText(
    nativeBlock,
    totalSavingPercentage,
    translations = getTranslations()
  ) {
    const select = nativeBlock.querySelector('select');
    const selectedOption = select?.options?.[select.selectedIndex];

    if (selectedOption) {
      return localizeTotalSavingPlanLabel(
        selectedOption.value,
        selectedOption.textContent.trim(),
        totalSavingPercentage,
        translations
      );
    }

    const planText = nativeBlock.querySelector(
      '.kaching-bundles__selling-plan-select, [class*="selling-plan"], [class*="delivery"]'
    );
    if (planText?.textContent.trim()) return planText.textContent.trim();

    return translate('delivery_fallback', {}, translations);
  }

  function buildPurchaseOptions(skin, nativeBlock) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ff-kaching-purchase-options';
    wrapper.setAttribute('data-ff-kaching-purchase-options', '');

    const oneTime = document.createElement('button');
    oneTime.type = 'button';
    oneTime.className =
      'ff-kaching-purchase-option ff-kaching-purchase-option--one-time';
    oneTime.innerHTML = `
      <span class="ff-kaching-purchase-option__main">
        <span class="ff-kaching-purchase-option__radio" aria-hidden="true"></span>
        <span class="ff-kaching-purchase-option__copy">
          <span class="ff-kaching-purchase-option__title"></span>
        </span>
        <span class="ff-kaching-purchase-option__price"></span>
      </span>
    `;

    const subscription = document.createElement('div');
    subscription.className =
      'ff-kaching-purchase-option ff-kaching-purchase-option--subscription';
    subscription.innerHTML = `
      <span class="ff-kaching-purchase-option__badge"></span>
      <button class="ff-kaching-purchase-option__main" type="button">
        <span class="ff-kaching-purchase-option__radio" aria-hidden="true"></span>
        <span class="ff-kaching-purchase-option__copy">
          <span class="ff-kaching-purchase-option__title"></span>
          <span class="ff-kaching-purchase-option__saving"></span>
        </span>
        <span aria-hidden="true"></span>
      </button>
      <span class="ff-kaching-purchase-option__delivery">
        <span class="ff-kaching-purchase-option__chevron" aria-hidden="true"></span>
        <span class="ff-kaching-purchase-option__delivery-label"><span class="ff-kaching-purchase-option__delivery-prefix"></span> <strong></strong></span>
        <span class="ff-kaching-purchase-option__delivery-price"></span>
      </span>
    `;

    oneTime.addEventListener('click', () => {
      setNativeSubscriptionSelected(nativeBlock, false);
      window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 0);
      window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 120);
    });

    subscription
      .querySelector('.ff-kaching-purchase-option__main')
      ?.addEventListener('click', () => {
        setNativeSubscriptionSelected(nativeBlock, true);
        window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 0);
        window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 120);
      });

    wrapper.append(oneTime, subscription);
    nativeBlock.before(wrapper);
    return wrapper;
  }

  function syncPlanSelect(nativeBlock, wrapper, totalSavingPercentage) {
    const nativeSelect = nativeBlock.querySelector('select');
    const delivery = wrapper.querySelector('.ff-kaching-purchase-option__delivery');
    let mirrorSelect = wrapper.querySelector('.ff-kaching-purchase-option__select');

    if (!nativeSelect || !delivery) {
      mirrorSelect?.remove();
      return;
    }

    if (!mirrorSelect) {
      mirrorSelect = document.createElement('select');
      mirrorSelect.className = 'ff-kaching-purchase-option__select';
      mirrorSelect.setAttribute('aria-label', translate('delivery_frequency_label'));
      mirrorSelect.addEventListener('change', () => {
        nativeSelect.value = mirrorSelect.value;
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        const skin = nativeBlock.closest(SKIN_SELECTOR);
        const wrapper = skin?.querySelector('[data-ff-kaching-purchase-options]');
        if (skin && wrapper) {
          window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 0);
          window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 120);
        }
      });
      delivery.appendChild(mirrorSelect);
    }

    const nativeSignature = Array.from(nativeSelect.options)
      .map((option) => `${option.value}:${option.textContent}`)
      .join('|');
    const displaySignature = `${totalSavingPercentage ?? 'native'}|${nativeSignature}`;
    const translations = getTranslations();

    if (mirrorSelect.dataset.nativeOptions !== displaySignature) {
      mirrorSelect.replaceChildren(
        ...Array.from(nativeSelect.options).map((option) => {
          const clone = document.createElement('option');
          clone.value = option.value;
          clone.textContent = localizeTotalSavingPlanLabel(
            option.value,
            option.textContent.trim(),
            totalSavingPercentage,
            translations
          );
          return clone;
        })
      );
      mirrorSelect.dataset.nativeOptions = displaySignature;
    }
    mirrorSelect.value = nativeSelect.value;
  }

  function ensureOneTimePriceCached(skin, nativeBlock, wrapper) {
    if (wrapper.dataset.oneTimePrice || wrapper.dataset.ffOneTimeCaptureAttempted) return;

    wrapper.dataset.ffOneTimeCaptureAttempted = 'true';

    if (!isNativeSubscriptionSelected(nativeBlock)) {
      const { current } = getBundlePrices(skin);
      if (current) wrapper.dataset.oneTimePrice = current;
      return;
    }

    setNativeSubscriptionSelected(nativeBlock, false);
    window.setTimeout(() => {
      const { current } = getBundlePrices(skin);
      if (current) wrapper.dataset.oneTimePrice = current;
      setNativeSubscriptionSelected(nativeBlock, true);
      window.setTimeout(() => syncCheckboxLayout(skin, nativeBlock, wrapper), 0);
    }, 120);
  }

  function syncCheckboxLayout(skin, nativeBlock, wrapper) {
    ensureOneTimePriceCached(skin, nativeBlock, wrapper);
    const translations = getTranslations();
    const isSubscription = isNativeSubscriptionSelected(nativeBlock);
    const oneTime = wrapper.querySelector('.ff-kaching-purchase-option--one-time');
    const subscription = wrapper.querySelector(
      '.ff-kaching-purchase-option--subscription'
    );
    const nativeTitle =
      nativeBlock.querySelector(
        '.kaching-bundles__subscriptions__title, .kaching-bundles__bar-title, [class*="subscription"][class*="title"]'
      )?.textContent.trim() ||
      nativeBlock.textContent.match(/subscribe[^%\n]*(?:\d+(?:[.,]\d+)?\s*%)?/i)?.[0]?.trim() ||
      translate('subscribe_and_save', {}, translations);
    const { oneTimePrice, subscriptionPrice } = getPurchaseOptionPrices(
      skin,
      nativeBlock,
      wrapper,
      nativeTitle
    );
    const totalSavingPercentage = getTotalSavingPercentage(skin, subscriptionPrice);

    oneTime?.classList.toggle('is-selected', !isSubscription);
    subscription?.classList.toggle('is-selected', isSubscription);
    oneTime?.setAttribute('aria-pressed', String(!isSubscription));
    subscription?.setAttribute('data-selected', String(isSubscription));

    setText(
      oneTime?.querySelector('.ff-kaching-purchase-option__title'),
      translate('one_time', {}, translations)
    );
    setText(
      subscription?.querySelector('.ff-kaching-purchase-option__title'),
      cleanSubscriptionTitle(nativeTitle, translations)
    );
    setText(
      subscription?.querySelector('.ff-kaching-purchase-option__saving'),
      getTotalSavingText(skin, subscriptionPrice, nativeTitle, translations)
    );
    setText(
      subscription?.querySelector('.ff-kaching-purchase-option__badge'),
      getBestValueBadge(skin, translations)
    );
    setPriceText(oneTime?.querySelector('.ff-kaching-purchase-option__price'), oneTimePrice);
    setPriceText(
      subscription?.querySelector('.ff-kaching-purchase-option__delivery-price'),
      subscriptionPrice
    );
    setText(
      subscription?.querySelector('.ff-kaching-purchase-option__delivery-prefix'),
      translate('delivery_label', {}, translations)
    );
    setText(
      subscription?.querySelector('.ff-kaching-purchase-option__delivery-label strong'),
      getDeliveryText(nativeBlock, totalSavingPercentage, translations)
    );
    syncPlanSelect(nativeBlock, wrapper, totalSavingPercentage);
  }

  function decorateCheckboxLayout(skin) {
    const nativeBlock = skin.querySelector(
      '.kaching-bundles__subscriptions--checkbox'
    );
    if (!nativeBlock) return;

    nativeBlock.classList.add('ff-kaching-native-checkbox');
    skin.classList.add('ff-kaching-checkbox-layout');

    const wrapper =
      skin.querySelector('[data-ff-kaching-purchase-options]') ||
      buildPurchaseOptions(skin, nativeBlock);
    syncCheckboxLayout(skin, nativeBlock, wrapper);
  }

  function decorateSkin(skin) {
    decorateCheckboxLayout(skin);

    const subscriptions = skin.querySelector(SUBSCRIPTIONS_SELECTOR);
    if (!subscriptions) return;

    const cards = Array.from(subscriptions.children).filter((element) =>
      element.classList.contains('kaching-bundles__bar')
    );

    if (cards.length !== 2) return;

    const settings = getSettings(skin);
    const subscribeByDefault = Boolean(settings?.subscriptions?.subscribeByDefault);
    const subscribeCard = cards[subscribeByDefault ? 0 : 1];
    const oneTimeCard = cards[subscribeByDefault ? 1 : 0];

    cards.forEach((card) => {
      card.classList.remove('ff-kaching-one-time', 'ff-kaching-subscription');
      card.removeAttribute('data-ff-kaching-badge');
    });

    oneTimeCard.classList.add('ff-kaching-one-time');
    subscribeCard.classList.add('ff-kaching-subscription');

    const badgeText = getBadgeText(skin);
    if (badgeText) {
      subscribeCard.setAttribute('data-ff-kaching-badge', badgeText);
    }
  }

  function decorateAll(root = document) {
    if (root.matches?.(SKIN_SELECTOR)) {
      decorateSkin(root);
    }

    root.querySelectorAll?.(SKIN_SELECTOR).forEach(decorateSkin);
  }

  function shouldIgnoreMutation(mutation) {
    const target = mutation.target;
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest('[data-ff-kaching-purchase-options]') ||
        target.closest('.ff-kaching-purchase-option')
    );
  }

  function init() {
    decorateAll();

    const observer = new MutationObserver((mutations) => {
      const shouldDecorate = mutations.some(
        (mutation) =>
          mutation.type === 'childList' &&
          (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) &&
          !shouldIgnoreMutation(mutation)
      );

      if (shouldDecorate) decorateAll();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener('shopify:section:load', (event) => {
      decorateAll(event.target);
    });

    document.addEventListener('kaching-bundles-block-loaded', () => {
      decorateAll();
    });

    document.addEventListener(
      'change',
      (event) => {
        const skin = event.target.closest?.(SKIN_SELECTOR);
        if (skin) window.setTimeout(() => decorateSkin(skin), 0);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const skin = event.target.closest?.(SKIN_SELECTOR);
        if (skin) window.setTimeout(() => decorateSkin(skin), 0);
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
